import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import Header from "../../components/header/header"
import Footer from "../../components/footer/footer"
import { links } from "../../components/shared/constants/constants"
import { PRICING_PLANS_CONTENT, planModulesMonthly } from "../../shared/pricingPlansContent"
import styles from "./pricing-page.module.css"
import pageShell from "../../shared/pageShell.module.css"
import startupsLandingStyles from "../../components/startups-landing/startups-landing-sections.module.css"

/** Same copy as main landing — see shared/pricingPlansContent.js */
const PLANS = PRICING_PLANS_CONTENT

/** Credits offer: monthly price is added to plan base. Saving vs baseline per product data. */
const AI_CREDIT_TIERS = [
	{
		id: "1-6mo",
		creditsPerMonth: 1,
		frequency: "1 / 6 months",
		monthlyPrice: 100,
		pricePerCredit: 600,
		saveAmount: 0,
		savePct: 0,
		includedInPlan: true,
	},
	{
		id: "1-3mo",
		creditsPerMonth: 1,
		frequency: "1 / 3 months",
		monthlyPrice: 200,
		pricePerCredit: 600,
		saveAmount: 0,
		savePct: 0,
	},
	{
		id: "1-mo",
		creditsPerMonth: 1,
		frequency: "1 / month",
		monthlyPrice: 600,
		pricePerCredit: 600,
		saveAmount: 0,
		savePct: 0,
	},
	{
		id: "2-mo",
		creditsPerMonth: 2,
		frequency: "1 / month",
		monthlyPrice: 1100,
		pricePerCredit: 550,
		saveAmount: 100,
		savePct: 8,
	},
	{
		id: "3-mo",
		creditsPerMonth: 3,
		frequency: "1 / month",
		monthlyPrice: 1500,
		pricePerCredit: 500,
		saveAmount: 300,
		savePct: 17,
	},
	{
		id: "4-mo",
		creditsPerMonth: 4,
		frequency: "1 / month",
		monthlyPrice: 1800,
		pricePerCredit: 450,
		saveAmount: 600,
		savePct: 25,
	},
	{
		id: "5-mo",
		creditsPerMonth: 5,
		frequency: "1 / month",
		monthlyPrice: 2125,
		pricePerCredit: 425,
		saveAmount: 875,
		savePct: 29,
	},
	{
		id: "6-mo",
		creditsPerMonth: 6,
		frequency: "1 / month",
		monthlyPrice: 2460,
		pricePerCredit: 410,
		saveAmount: 1140,
		savePct: 32,
	},
	{
		id: "10-mo",
		creditsPerMonth: 10,
		frequency: "1 / month",
		monthlyPrice: 4000,
		pricePerCredit: 400,
		saveAmount: 2000,
		savePct: 33,
	},
]

const DEFAULT_AI_TIER_ID = AI_CREDIT_TIERS[0].id

function getAiTier(tierId) {
	return AI_CREDIT_TIERS.find((t) => t.id === tierId) ?? AI_CREDIT_TIERS[0]
}

function formatUsdInt(n) {
	return n.toLocaleString("en-US", { maximumFractionDigits: 0 })
}

/** Select + breakdown: included tier shows "… · included in plan" instead of $/mo. */
function tierOptionLabel(t) {
	let base
	if (t.frequency === "1 / 6 months") base = "1 credit per 6 months"
	else if (t.frequency === "1 / 3 months") base = "1 credit per 3 months"
	else if (t.frequency === "1 / month") {
		const n = t.creditsPerMonth
		base = `${n} credit${n === 1 ? "" : "s"} per month`
	} else {
		const n = t.creditsPerMonth
		base = `${n} credit${n === 1 ? "" : "s"} per ${t.frequency}`
	}
	if (t.includedInPlan) return `${base} · included in plan`
	return `${base} · $${formatUsdInt(t.monthlyPrice)}/mo`
}

const AiCreditPicker = ({ value, onChange, planId, planName }) => {
	const [open, setOpen] = useState(false)
	const wrapRef = useRef(null)
	const selected = getAiTier(value)
	const labelId = `ai-credit-label-${planId}`

	useEffect(() => {
		if (!open) return
		const onDoc = (e) => {
			if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false)
		}
		const onKey = (e) => {
			if (e.key === "Escape") setOpen(false)
		}
		document.addEventListener("mousedown", onDoc)
		document.addEventListener("keydown", onKey)
		return () => {
			document.removeEventListener("mousedown", onDoc)
			document.removeEventListener("keydown", onKey)
		}
	}, [open])

	return (
		<div className={styles.creditPicker} ref={wrapRef}>
			<button
				type="button"
				className={`${styles.creditPickerTrigger} ${styles.creditPickerTriggerAi}`}
				aria-haspopup="listbox"
				aria-expanded={open}
				aria-labelledby={labelId}
				aria-label={`${planName}: choose AI sourcing credits`}
				onClick={(e) => {
					e.stopPropagation()
					setOpen((o) => !o)
				}}
			>
				<span className={styles.creditPickerTriggerText}>
					<span id={labelId} className={styles.creditPickerLabelInline}>
						AI Sourcing credits
					</span>
					<span className={styles.creditPickerValue}>
						<span className={styles.creditPickerValueRow}>
							<span>{tierOptionLabel(selected)}</span>
							{selected.saveAmount > 0 ? (
								<span className={styles.saveTagInline}>Save ${formatUsdInt(selected.saveAmount)}</span>
							) : null}
						</span>
					</span>
				</span>
				<span className={styles.creditPickerChev} aria-hidden />
			</button>
			{open ? (
				<ul className={styles.creditPickerMenu} role="listbox" aria-labelledby={labelId}>
					{AI_CREDIT_TIERS.map((t) => (
						<li key={t.id} role="none">
							<button
								type="button"
								role="option"
								aria-selected={t.id === value}
								className={`${styles.creditPickerOption} ${t.id === value ? styles.creditPickerOptionActive : ""}`}
								onClick={(e) => {
									e.stopPropagation()
									onChange(t.id)
									setOpen(false)
								}}
							>
								<span className={styles.creditPickerOptionInner}>
									<span className={styles.creditPickerOptionText}>{tierOptionLabel(t)}</span>
									{t.saveAmount > 0 ? (
										<span className={styles.saveTag}>Save ${formatUsdInt(t.saveAmount)}</span>
									) : null}
								</span>
							</button>
						</li>
					))}
				</ul>
			) : null}
		</div>
	)
}

/** PeopleForce-style seat bands — billedSeats drives per-user add-on math (min. 25 seats). */
const USER_TIERS = [
	{ id: "t1", label: "1–25 active users", billedSeats: 25 },
	{ id: "t2", label: "26–50 active users", billedSeats: 50 },
	{ id: "t3", label: "51–100 active users", billedSeats: 100 },
	{ id: "t4", label: "101–250 active users", billedSeats: 250 },
	{ id: "t5", label: "251+ active users", billedSeats: 400 },
]

const TeamSizePicker = ({ value, onChange }) => {
	const [open, setOpen] = useState(false)
	const wrapRef = useRef(null)
	const selected = USER_TIERS.find((t) => t.id === value) ?? USER_TIERS[0]
	const labelId = "dock-user-tier-label"

	useEffect(() => {
		if (!open) return
		const onDoc = (e) => {
			if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false)
		}
		const onKey = (e) => {
			if (e.key === "Escape") setOpen(false)
		}
		document.addEventListener("mousedown", onDoc)
		document.addEventListener("keydown", onKey)
		return () => {
			document.removeEventListener("mousedown", onDoc)
			document.removeEventListener("keydown", onKey)
		}
	}, [open])

	return (
		<div
			className={`${styles.creditPicker} ${styles.tierPicker} ${open ? styles.tierPickerOpen : ""}`}
			ref={wrapRef}
		>
			<span id={labelId} className={styles.creditPickerLabel}>
				Team size
			</span>
			<button
				type="button"
				className={styles.creditPickerTrigger}
				aria-haspopup="listbox"
				aria-expanded={open}
				aria-labelledby={labelId}
				aria-label="Choose team size band"
				onClick={() => setOpen((o) => !o)}
			>
				<span className={styles.creditPickerValue}>{selected.label}</span>
				<span className={styles.creditPickerChev} aria-hidden />
			</button>
			{open ? (
				<ul className={styles.creditPickerMenu} role="listbox" aria-labelledby={labelId}>
					{USER_TIERS.map((t) => (
						<li key={t.id} role="none">
							<button
								type="button"
								role="option"
								aria-selected={t.id === value}
								className={`${styles.creditPickerOption} ${t.id === value ? styles.creditPickerOptionActive : ""}`}
								onClick={() => {
									onChange(t.id)
									setOpen(false)
								}}
							>
								{t.label}
							</button>
						</li>
					))}
				</ul>
			) : null}
		</div>
	)
}

const ADDONS = {
	vacations: {
		id: "vacations",
		name: "Vacations",
		desc: "Request, approve, and balance PTO with policies that match each region.",
		emoji: "🏖️",
		iconBg: "#e8f4fc",
		flat: 18,
		perUser: 0.75,
		minBilledUsers: 25,
		features: [
			"Time-off requests & approvals",
			"Balance & accrual rules",
			"Team calendars",
			"Policy templates",
			"Email reminders",
		],
	},
	time: {
		id: "time",
		name: "Time tracker",
		desc: "Log hours by project or task — approvals and exports built in.",
		emoji: "⏱️",
		iconBg: "#e4f7ef",
		flat: 19,
		perUser: 0.8,
		minBilledUsers: 25,
		features: [
			"Timers & manual entries",
			"Weekly timesheets",
			"Manager approval flows",
			"Billable vs non-billable",
			"CSV / payroll-ready export",
		],
	},
	projects: {
		id: "projects",
		name: "Projects",
		desc: "Plan work, assign owners, and track delivery without leaving Techcore.",
		emoji: "📁",
		iconBg: "#f0edff",
		flat: 24,
		perUser: 1.0,
		minBilledUsers: 25,
		features: [
			"Project & milestone views",
			"Task lists & owners",
			"Status & deadlines",
			"Cross-team visibility",
			"Lightweight reporting",
		],
	},
	performance: {
		id: "performance",
		name: "Performance",
		desc: "Goals, check-ins, and review cycles tied to real work outcomes.",
		emoji: "📊",
		iconBg: "#fff4e0",
		flat: 29,
		perUser: 1.2,
		minBilledUsers: 25,
		features: [
			"Goals & OKRs",
			"1:1 and review templates",
			"Peer & manager feedback",
			"Cycle automation",
			"History per person",
		],
	},
	compensation: {
		id: "compensation",
		name: "Compensation",
		desc: "Bands, benchmarks, and change history in one place for HR and managers.",
		emoji: "💵",
		iconBg: "#fdf2f8",
		flat: 32,
		perUser: 1.15,
		minBilledUsers: 25,
		features: [
			"Salary bands & ranges",
			"Merit & adjustment tracking",
			"Role-based visibility",
			"Audit-friendly history",
			"Integration-ready exports",
		],
	},
}

function formatMoney(n) {
	return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function addonMonthly(a, billedUsers) {
	return a.flat + a.perUser * billedUsers
}

const PricingPage = () => {
	useLayoutEffect(() => {
		window.scrollTo({ top: 0, left: 0, behavior: "auto" })
	}, [])

	const [planId, setPlanId] = useState("starter")
	const [aiByPlan, setAiByPlan] = useState(() => ({
		starter: DEFAULT_AI_TIER_ID,
		growth: DEFAULT_AI_TIER_ID,
	}))
	const [userTierId, setUserTierId] = useState("t1")
	const [addons, setAddons] = useState(() => new Set())
	const [openFeatures, setOpenFeatures] = useState(null)

	const userTier = USER_TIERS.find((t) => t.id === userTierId) || USER_TIERS[0]
	const plan = PLANS[planId]
	const aiTier = getAiTier(aiByPlan[planId] ?? DEFAULT_AI_TIER_ID)
	const creditMonthly = aiTier.monthlyPrice

	const billedUsers = useMemo(() => Math.max(userTier.billedSeats, 25), [userTier.billedSeats])
	const modulesMonthly = useMemo(() => planModulesMonthly(plan, billedUsers), [plan, billedUsers])

	const totals = useMemo(() => {
		const base = modulesMonthly + creditMonthly
		let addonsSum = 0
		const lines = []
		Object.values(ADDONS).forEach((a) => {
			if (!addons.has(a.id)) return
			const sub = addonMonthly(a, billedUsers)
			addonsSum += sub
			lines.push({
				id: a.id,
				label: a.name,
				flat: a.flat,
				perUser: a.perUser,
				sub,
			})
		})
		return { base, addonsSum, total: base + addonsSum, addonLines: lines }
	}, [modulesMonthly, creditMonthly, addons, billedUsers])

	const estimateMetaTail = [
		userTier.label,
		addons.size ? `${addons.size} add-on${addons.size > 1 ? "s" : ""}` : null,
	]
		.filter(Boolean)
		.join(" · ")

	const toggleAddon = (id) => {
		setAddons((prev) => {
			const next = new Set(prev)
			if (next.has(id)) next.delete(id)
			else next.add(id)
			return next
		})
	}

	const handleSignup = () => {
		const params = new URLSearchParams({
			plan: planId,
			users: String(userTier.billedSeats),
		})
		params.set("aiTier", aiTier.id)
		params.set("aiMonthly", String(creditMonthly))
		params.set("modulesMonthly", String(modulesMonthly))
		if (addons.size) params.set("addons", [...addons].join(","))
		window.location.href = `${links.startupsSignUp}?${params.toString()}`
	}

	return (
		<div className={`${pageShell.page} ${styles.page}`}>
			<Header />
			<div className={styles.pricingSurface}>
				<section className={styles.pricingIntroSection} aria-labelledby="pricing-hero-heading">
					<div className={`container ${startupsLandingStyles.sectionContainer} ${styles.heroShell}`}>
						<div className={startupsLandingStyles.label}>Pricing</div>
						<h1 id="pricing-hero-heading" className={styles.heroTitle}>
							Build the plan that fits your team
						</h1>
						<p className={styles.heroNote}>
							Start by choosing Starter or Growth; tune credits and add-ons below — your estimate updates as you go.
						</p>
					</div>
				</section>

				<div className={`container ${startupsLandingStyles.sectionContainer} ${styles.pricingPageWrap}`}>
				<div className={styles.pricingPageGrid}>
					<div className={`${startupsLandingStyles.startupsLandingRoot} ${styles.pricingMain}`}>
						<section className={styles.sectionBlock} aria-labelledby="plans-heading">
							<div id="plans-heading" className={startupsLandingStyles.label}>
								Plans
							</div>

							<div className={styles.plansLandingWrap}>
								<div className={`${startupsLandingStyles.pricingGrid} ${styles.pricingGridMain}`}>
								{Object.values(PLANS).map((p) => {
									const selected = planId === p.id
									const aiVal = aiByPlan[p.id] ?? DEFAULT_AI_TIER_ID
									const cardAiTier = getAiTier(aiVal)
									const cardModules = planModulesMonthly(p, billedUsers)
									const cardMonthlyTotal = cardModules + cardAiTier.monthlyPrice
									return (
										<div
											key={p.id}
											role="button"
											tabIndex={0}
											className={`
												${startupsLandingStyles.planCard}
												${styles.planSelectBtn}
												${styles.planCardPricing}
												${selected ? styles.planCardActive : ""}
											`.trim()}
											onClick={() => setPlanId(p.id)}
											onKeyDown={(e) => {
												if (e.key === "Enter" || e.key === " ") {
													e.preventDefault()
													setPlanId(p.id)
												}
											}}
											aria-pressed={selected}
										>
											<span className={styles.planRadio} aria-hidden>
												<span className={styles.planRadioDot} />
											</span>
											<div className={startupsLandingStyles.planName}>{p.name}</div>
											<div className={startupsLandingStyles.planPrice}>
												${formatUsdInt(cardMonthlyTotal)} <span>/ mo</span>
											</div>
											<div className={styles.planCreditPricing} onClick={(e) => e.stopPropagation()}>
												<AiCreditPicker
													planId={p.id}
													planName={p.name}
													value={aiVal}
													onChange={(v) =>
														setAiByPlan((prev) => ({
															...prev,
															[p.id]: v,
														}))
													}
												/>
											</div>
											<ul className={`${startupsLandingStyles.planFeatures} ${styles.planFeaturesPricing}`}>
												{p.features.map((line) => (
													<li key={line}>
														<span className={startupsLandingStyles.check}>✓</span> {line}
													</li>
												))}
											</ul>
										</div>
									)
								})}
								</div>
							</div>
						</section>

						<section className={styles.sectionBlock} aria-labelledby="addons-heading">
							<div id="addons-heading" className={startupsLandingStyles.label}>
								Add-ons
							</div>

							<div className={styles.addonsList}>
							{Object.values(ADDONS).map((a) => {
								const selected = addons.has(a.id)
								const open = openFeatures === a.id
								return (
									<div key={a.id} className={`${styles.addonCard} ${selected ? styles.addonCardSelected : ""}`}>
										<div
											className={`${styles.addonMain} ${open ? styles.addonMainFeaturesOpen : ""}`}
											role="button"
											tabIndex={0}
											onClick={() => toggleAddon(a.id)}
											onKeyDown={(e) => {
												if (e.key === "Enter" || e.key === " ") {
													e.preventDefault()
													toggleAddon(a.id)
												}
											}}
										>
											<span className={styles.addonIcon} style={{ background: a.iconBg }}>
												{a.emoji}
											</span>
											<span className={styles.addonBody}>
												<span className={styles.addonName}>{a.name}</span>
												<span className={styles.addonDesc}>{a.desc}</span>
												<span className={styles.addonMore}>
													<button
														type="button"
														className={styles.btnFeatures}
														onClick={(e) => {
															e.stopPropagation()
															setOpenFeatures(open ? null : a.id)
														}}
													>
														{open ? "Hide details" : "More details"}
													</button>
												</span>
											</span>
											<span className={styles.addonRight}>
												<span className={styles.addonPriceBlock}>
													<span className={styles.apmFlat}>+${a.flat} / mo</span>
													<span className={styles.apmPer}>${formatMoney(a.perUser)} / seat</span>
													<span className={styles.apmTier}>
														<span className={styles.apmTierDot} />
														Min. {a.minBilledUsers} seats billed
													</span>
												</span>
											</span>
											<span className={styles.addonChk} aria-hidden>
												<span className={styles.chkTick}>✓</span>
											</span>
											{open && (
												<ul className={styles.addonFeaturesPopup}>
													{a.features.map((f) => (
														<li key={f}>
															<span className={`${startupsLandingStyles.check} ${styles.addonFeatureCheck}`} aria-hidden>
																✓
															</span>
															<span className={styles.addonFeatureText}>{f}</span>
														</li>
													))}
												</ul>
											)}
										</div>
									</div>
								)
							})}
							</div>
						</section>
					</div>

					<div className={styles.estimateColumn}>
						<aside
							className={styles.estimateAside}
							role="region"
							aria-label="Estimated monthly total"
							aria-live="polite"
						>
							<div className={styles.estimatePanel}>
							<div className={styles.estimatePanelHeader}>
								<span className={styles.estimatePanelLabel}>Estimated monthly total</span>
								<div className={styles.estimatePanelPlanMark}>
									<span className={styles.estimatePanelPlanName}>{plan.name}</span>
									{estimateMetaTail ? (
										<p className={styles.estimatePanelMeta}>{estimateMetaTail}</p>
									) : null}
								</div>
							</div>
							<div className={styles.estimatePanelTier}>
								<TeamSizePicker value={userTierId} onChange={setUserTierId} />
							</div>
							<div className={styles.estimatePanelPriceRow}>
								<div className={styles.estimatePanelPrice}>
									<span className={styles.estimatePanelAmount}>${formatMoney(totals.total)}</span>
									<span className={styles.estimatePanelPeriod}>/ month</span>
								</div>
								<div className={styles.estimatePanelActions}>
									<a href="mailto:support@techcore.io" className={styles.panelBtnGhost}>
										Sales
									</a>
									<button type="button" className={styles.panelBtnPrimary} onClick={handleSignup}>
										Continue
									</button>
								</div>
							</div>

							<details className={styles.breakdownDetails}>
								<summary className={styles.breakdownSummary}>View breakdown</summary>
								<div className={styles.breakdownPanel}>
									<div className={styles.breakdownRow}>
										<span>
											{plan.modulesPerUserMonthly > 0
												? `Base modules ($${formatUsdInt(plan.modulesPerUserMonthly)} × ${billedUsers} users)`
												: "Base modules"}
										</span>
										<span>${formatMoney(modulesMonthly)}/mo</span>
									</div>
									<div className={styles.breakdownBlock}>
										<div className={styles.breakdownRow}>
											<span>AI Sourcing credits</span>
											<span>+${formatMoney(creditMonthly)}/mo</span>
										</div>
										<p className={styles.breakdownHint}>{tierOptionLabel(aiTier)}</p>
									</div>
									<div className={styles.breakdownRow}>
										<span>Team size</span>
										<span>{userTier.label}</span>
									</div>
									<div className={styles.breakdownRow}>
										<span>Active users (add-on billing)</span>
										<span>{billedUsers}</span>
									</div>
									{totals.addonLines.length === 0 ? (
										<p className={styles.breakdownEmpty}>No add-ons selected</p>
									) : (
										totals.addonLines.map((line) => (
											<div key={line.id} className={styles.breakdownBlock}>
												<div className={styles.breakdownRow}>
													<span>{line.label}</span>
													<span>${formatMoney(line.sub)}/mo</span>
												</div>
												<p className={styles.breakdownHint}>
													${line.flat} flat + ${formatMoney(line.perUser)} × {billedUsers} seats
												</p>
											</div>
										))
									)}
									<p className={styles.breakdownNote}>
										Estimates are illustrative. Taxes may apply. Final billing is confirmed at checkout.
									</p>
								</div>
							</details>
							</div>
						</aside>
					</div>
				</div>
			</div>
			</div>

			<Footer />
		</div>
	)
}

export default PricingPage
